export type AddressPrefix = 'pickup' | 'destination' | 'origin'

export type GoogleAddressComponent = {
  long_name?: string
  short_name?: string
  longText?: string
  shortText?: string
  types: string[]
}

export type GooglePlace = {
  address_components?: GoogleAddressComponent[]
  addressComponents?: GoogleAddressComponent[]
  fetchFields?: (request: { fields: string[] }) => Promise<void>
}

export type GoogleAutocomplete = {
  addListener: (
    eventName: 'place_changed',
    handler: () => void
  ) => { remove: () => void }
  getPlace: () => GooglePlace
}

export type GooglePlacePrediction = {
  text: {
    toString: () => string
  }
  placeId?: string
  toPlace: () => GooglePlace
}

export type GooglePlaceSuggestion = {
  placePrediction?: GooglePlacePrediction
}

export type GoogleAutocompleteSuggestion = {
  fetchAutocompleteSuggestions: (request: Record<string, unknown>) => Promise<{
    suggestions: GooglePlaceSuggestion[]
  }>
}

declare global {
  interface Window {
    google?: {
      maps: {
        importLibrary: (libraryName: 'places') => Promise<{
          AutocompleteSuggestion: GoogleAutocompleteSuggestion
          AutocompleteSessionToken: new () => unknown
        }>
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: Record<string, unknown>
          ) => GoogleAutocomplete
        }
      }
    }
  }
}

const getAddressComponent = (
  components: GoogleAddressComponent[],
  type: string,
  name: 'long_name' | 'short_name' = 'long_name'
) => {
  const component = components.find((item) => item.types.includes(type))
  if (!component) return ''

  if (name === 'short_name') {
    return component.short_name ?? component.shortText ?? ''
  }

  return component.long_name ?? component.longText ?? ''
}

export const parseGoogleAddress = (place: GooglePlace) => {
  const components = place.address_components ?? place.addressComponents ?? []
  const street = [
    getAddressComponent(components, 'street_number'),
    getAddressComponent(components, 'route'),
  ]
    .filter(Boolean)
    .join(' ')

  return {
    street,
    city:
      getAddressComponent(components, 'locality') ||
      getAddressComponent(components, 'postal_town') ||
      getAddressComponent(components, 'sublocality') ||
      getAddressComponent(components, 'administrative_area_level_2'),
    state: getAddressComponent(
      components,
      'administrative_area_level_1',
      'short_name'
    ),
    zip: getAddressComponent(components, 'postal_code', 'short_name'),
    country: getAddressComponent(components, 'country', 'short_name') || 'US',
  }
}

export const normalizeZip = (zip?: number | string) =>
  zip?.toString().match(/\d{5}/)?.[0] ?? ''
